import { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Box } from '@theme-ui/components';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';

import CrossIcon from '@app/assets/icons/CrossIcon';
import { H3 } from '@app/components/typography';
import { closeModal } from '@app/redux/modal';
import theme from '@app/styles/theme';
import { rem } from '@app/styles/theme/layout';

import {
  useReturnRequestLazyQuery,
  type ReturnRequestQueryVariables,
} from '../__graphql__/ReturnRequest';
import type { OrderHistoryOrderFragment } from '../../__graphql__/OrderHistory';
import type {
  OrderItemFragment,
  OrderItemProductFragment,
} from '../../OrderHistoryDetail/__graphql__/OrderHistoryDetailContext';
import { ORDER_RETURN_REQUEST_FAST_TRACK_PERIOD } from '../constants';
import messages from '../ReturnRequest.messages';
import AdditionalReviewProccesInfo from './AdditionalReviewProccesInfo';
import ReturnRequestForm from './ReturnRequestForm';
import { CloseButton, ModalWrapper } from './ReturnRequestModal.styles';

export type ReturnRequestModalProps = {
  orderDate: string;
  orderUid: string;
  orderProducts:
    | OrderHistoryOrderFragment['items']
    | OrderItemFragment['items'];
  contactEmail: string;
  onSuccess: () => void;
  onError: () => void;
};

const ReturnRequestModal = ({
  orderDate,
  orderProducts,
  contactEmail,
  orderUid,
  onSuccess,
  onError,
}: ReturnRequestModalProps) => {
  const [submitReturnRequest, { loading, error }] = useReturnRequestLazyQuery();

  const dispatch = useDispatch();
  const [showAdditionalReviewInfo, setShowAdditionalReviewInfo] =
    useState(true);

  const hasExceededFastTrack =
    differenceInCalendarDays(Date.now(), new Date(orderDate)) >
    ORDER_RETURN_REQUEST_FAST_TRACK_PERIOD;

  useEffect(() => {
    if (!hasExceededFastTrack) {
      setShowAdditionalReviewInfo(false);
    }
  }, [hasExceededFastTrack]);

  const formOrderProducts = orderProducts
    ?.filter((item): item is NonNullable<typeof item> => Boolean(item))
    .map((item) => {
      const {
        id,
        status,
        productSku,
        productName,
        eligibleForReturn,
        returnStatus,
      } = item as OrderItemProductFragment;
      return {
        id,
        status,
        productSku,
        productName,
        eligibleForReturn,
        returnStatus,
      };
    });

  const handleSubmitReturnRequest = async (
    formPayload: Omit<ReturnRequestQueryVariables, 'orderUid' | 'contactEmail'>,
  ) => {
    try {
      await submitReturnRequest({
        variables: {
          orderUid,
          contactEmail,
          returnReason: formPayload.returnReason,
          commentText: formPayload.commentText,
          items: formPayload.items,
        },
      });
      onSuccess();
    } catch {
      onError();
    } finally {
      dispatch(closeModal());
    }
  };

  return (
    <ModalWrapper>
      <CloseButton
        data-testid="return-request-modal-close-button"
        onClick={() => dispatch(closeModal())}
      >
        <CrossIcon />
      </CloseButton>

      <H3 style={{ margin: `0 0 ${rem(14)} 0` }}>
        <FormattedMessage {...messages.modalHeading} />
      </H3>
      <Box
        sx={{
          borderBottom: `${rem(2)} solid ${theme.colors.lightGray}`,
          marginBottom: `${rem(24)}`,
          paddingBottom: `${rem(15)}`,
        }}
      >
        <FormattedMessage
          {...messages.orderDate}
          values={{
            date: (
              <strong>
                <FormattedDate
                  value={orderDate}
                  year="numeric"
                  month="long"
                  day="2-digit"
                />
              </strong>
            ),
          }}
        />
      </Box>
      {showAdditionalReviewInfo ? (
        <AdditionalReviewProccesInfo
          onContinue={() => setShowAdditionalReviewInfo(false)}
        />
      ) : (
        <ReturnRequestForm
          onSubmitSuccess={handleSubmitReturnRequest}
          products={formOrderProducts}
          requestLoading={loading}
          requestError={Boolean(error)}
        />
      )}
    </ModalWrapper>
  );
};

export { ReturnRequestModal };
